import React, { useState, useEffect, useRef, useMemo } from "react";
import { Modal, Button } from "react-bootstrap";
import "./../App.css";

function ProjectModal({
    onClose,
    title,
    description,
    imgUrl,
    link,
    videoUrl,
    additionalLinks,
    tech_definitions,
    languages_frameworks,
    setChatOpen,
    navigate,
    language,
    onShowNext,
    onShowPrevious,
    currentIndex,
    totalProjects,
    ...props
}) {
    const [showImageModal, setShowImageModal] = useState(false);
    const [isImageZoomed, setIsImageZoomed] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [zoomOffset, setZoomOffset] = useState({ x: 0, y: 0 });
    // eslint-disable-next-line no-unused-vars
    const [currentTechIndex, setCurrentTechIndex] = useState(0);
    const dragStateRef = useRef(null);
    const dragActivityRef = useRef(false);

    // Swipe gesture state
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);

    const techEntries = useMemo(() => (tech_definitions ? Object.entries(tech_definitions) : []), [tech_definitions]);
    const hasMultipleProjects = (typeof totalProjects === "number" && totalProjects > 1);
    const projectCounterText =
        typeof currentIndex === "number" && totalProjects && totalProjects > 0
            ? `${language === "es" ? "Proyecto" : "Project"} ${currentIndex + 1} / ${totalProjects}`
            : null;
    const handleNext = () => {
        if (!hasMultipleProjects) return;
        if (typeof onShowNext === "function") {
            onShowNext();
        }
    };
    const handlePrevious = () => {
        if (!hasMultipleProjects) return;
        if (typeof onShowPrevious === "function") {
            onShowPrevious();
        }
    };

    // Swipe gesture handlers
    const minSwipeDistance = 50;

    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe && hasMultipleProjects) {
            handleNext();
        }
        if (isRightSwipe && hasMultipleProjects) {
            handlePrevious();
        }
    };

    // Mouse swipe handlers for desktop testing
    const onMouseDown = (e) => {
        setTouchEnd(null);
        setTouchStart(e.clientX);
    };

    const onMouseMove = (e) => {
        if (touchStart !== null) {
            setTouchEnd(e.clientX);
        }
    };

    const onMouseUp = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe && hasMultipleProjects) {
            handleNext();
        }
        if (isRightSwipe && hasMultipleProjects) {
            handlePrevious();
        }
        setTouchStart(null);
        setTouchEnd(null);
    };

    const handleImageClick = () => {
        setZoomOffset({ x: 0, y: 0 });
        setIsImageZoomed(false);
        setIsDragging(false);
        dragActivityRef.current = false;
        dragStateRef.current = null;
        setShowImageModal(true);
    };

    const handleCloseImageModal = () => {
        setShowImageModal(false);
        setIsImageZoomed(false);
        setZoomOffset({ x: 0, y: 0 });
        setIsDragging(false);
        dragActivityRef.current = false;
        dragStateRef.current = null;
    };

    const handleZoomToggle = () => {
        dragStateRef.current = null;
        dragActivityRef.current = false;
        setIsImageZoomed(prev => {
            const next = !prev;
            setZoomOffset({ x: 0, y: 0 });
            if (!next) {
                setIsDragging(false);
            }
            return next;
        });
    };

    const handlePointerDown = event => {
        if (!isImageZoomed) {
            return;
        }

        event.preventDefault();
        setIsDragging(true);
        dragActivityRef.current = false;
        dragStateRef.current = {
            pointerId: event.pointerId,
            lastX: event.clientX,
            lastY: event.clientY
        };
        event.currentTarget.setPointerCapture?.(event.pointerId);
    };

    const handlePointerMove = event => {
        if (!isImageZoomed || !isDragging || !dragStateRef.current) {
            return;
        }

        event.preventDefault();
        const { lastX, lastY } = dragStateRef.current;
        const deltaX = event.clientX - lastX;
        const deltaY = event.clientY - lastY;

        if (deltaX !== 0 || deltaY !== 0) {
            dragActivityRef.current = true;
            dragStateRef.current = {
                pointerId: event.pointerId,
                lastX: event.clientX,
                lastY: event.clientY
            };

            setZoomOffset(prev => ({
                x: prev.x + deltaX,
                y: prev.y + deltaY
            }));
        }
    };

    const endPointerInteraction = event => {
        if (dragStateRef.current?.pointerId !== undefined) {
            event.currentTarget.releasePointerCapture?.(dragStateRef.current.pointerId);
        }
        setIsDragging(false);
        dragStateRef.current = null;
    };

    const handlePointerUp = event => {
        if (!isImageZoomed || !dragStateRef.current) {
            return;
        }

        endPointerInteraction(event);
    };

    const handlePointerLeave = event => {
        if (!isImageZoomed || !dragStateRef.current) {
            return;
        }

        dragActivityRef.current = false;
        endPointerInteraction(event);
    };

    const handlePointerCancel = event => {
        dragActivityRef.current = false;
        if (dragStateRef.current) {
            endPointerInteraction(event);
        }
    };

    const handleContainerClick = () => {
        if (dragActivityRef.current) {
            dragActivityRef.current = false;
            return;
        }
        handleZoomToggle();
    };

    useEffect(() => {
        setCurrentTechIndex(0);
        if (techEntries.length < 2) {
            return undefined;
        }

        const interval = setInterval(() => {
            setCurrentTechIndex(prev => (prev + 1) % techEntries.length);
        }, 10000);

        return () => clearInterval(interval);
    }, [techEntries]);

    useEffect(() => {
        setShowImageModal(false);
        setIsImageZoomed(false);
        setZoomOffset({ x: 0, y: 0 });
        setIsDragging(false);
        dragActivityRef.current = false;
        dragStateRef.current = null;
    }, [imgUrl]);

    return (
        <Modal show={true} onHide={onClose} centered size="xl" className="cyber-modal">
            <Modal.Header closeButton className="cyber-modal-header">
                <div className="modal-nav-container">
                    <button
                        type="button"
                        className="modal-nav-button previous"
                        onClick={handlePrevious}
                        disabled={!hasMultipleProjects}
                        aria-disabled={!hasMultipleProjects}
                        aria-label={language === "es" ? "Proyecto anterior" : "Previous project"}
                    >
                        <span aria-hidden="true">&#8249;</span>
                    </button>
                    <div className="modal-title-wrapper">
                        <Modal.Title className="modal-title">{title}</Modal.Title>
                        {projectCounterText && (
                            <span className="modal-project-counter">{projectCounterText}</span>
                        )}
                    </div>
                    <button
                        type="button"
                        className="modal-nav-button next"
                        onClick={handleNext}
                        disabled={!hasMultipleProjects}
                        aria-disabled={!hasMultipleProjects}
                        aria-label={language === "es" ? "Siguiente proyecto" : "Next project"}
                    >
                        <span aria-hidden="true">&#8250;</span>
                    </button>
                </div>
            </Modal.Header>

            <Modal.Body 
                className="cyber-body"
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
            >


                    
                <div className="summarize-button-container text-center">
                    <Button className="cyber-button summarize-button" onClick={() => navigate('/chat', { state: { prompt: language === 'es' ? `Explicame sobre el proyecto: ${title}` : `Explain me about this project: ${title}` } })}>
                        <span className="button-text">Explain with chatCJR</span>
                        <span className="button-glow"></span>
                    </Button>
                </div>


                <div className="holographic-wrapper">
                    <img 
                        src={imgUrl} 
                        alt={title} 
                        className="holographic-image clickable-image" 
                        onClick={handleImageClick}
                        style={{ cursor: 'pointer' }}
                    />
                </div>


                <div className="description-section">
                    <h3 className="section-title">PROJECT OVERVIEW</h3>
                    <div className="project-overview-card">
                        <div className="tech-card-content">{description}</div>
                    </div>
                </div>

                   {languages_frameworks && (
                    <div className="framework-carousel">
                        {languages_frameworks.map((logo, index) => (
                            <div key={index} className="framework-logo">
                                <img src={logo} alt="Tech logo" />
                            </div>
                        ))}
                    </div>
                )}

            {/*   {techEntries.length > 0 && (
                    <div className="tech-carousel-container">
                        <h3 className="section-title">TECH SPECS</h3>
                        <div className="tech-carousel-wrapper">
                            <div className="tech-carousel">
                                {techEntries.map(([tech, definition], index) => (
                                    <div
                                        key={index}
                                        className={`tech-card ${index === currentTechIndex ? 'active' : ''}`}
                                    >
                                        <div className="tech-card-header">{tech}</div>
                                        <div className="tech-card-content">{definition}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="carousel-indicators">
                            {techEntries.map((_, index) => (
                                <span
                                    key={index}
                                    className={`indicator ${index === currentTechIndex ? 'active' : ''}`}
                                    onClick={() => setCurrentTechIndex(index)}
                                />
                            ))}
                        </div>
                    </div>
                )}
            {/* Tech Specs Carousel */}

         

                <div className="link-panel">
                    {link && (
                        <Button className="cyber-button" onClick={() => window.open(link, "_blank")}>
                            <span className="button-text">ACCESS PROJECT</span>
                            <span className="button-glow"></span>
                        </Button>
                    )}
                    {additionalLinks && (
                        <div className="additional-links">
                            {additionalLinks.map((link, index) => (
                                <a key={index} href={link.url} className="cyber-link" target="_blank" rel="noopener noreferrer">
                                    <span className="link-decoration">⟫</span>
                                    {link.text}
                                </a>
                            ))}
                        </div>
                    )}
                </div>
            </Modal.Body>

            {/* Image Modal for full-size viewing */}
            <Modal show={showImageModal} onHide={handleCloseImageModal} centered size="xl" className="image-modal">
                <Modal.Header closeButton>
                    <Modal.Title>{title} - Full Image</Modal.Title>
                </Modal.Header>
                <Modal.Body className={`image-modal-body ${isImageZoomed ? 'zoomed' : ''}`}>
                    <div
                        className={`full-image-container ${isImageZoomed ? 'zoomed' : ''} ${isImageZoomed && isDragging ? 'dragging' : ''}`}
                        onClick={handleContainerClick}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        onPointerLeave={handlePointerLeave}
                        onPointerCancel={handlePointerCancel}
                        aria-label={isImageZoomed ? 'Click to exit zoom or drag to move image' : 'Click to zoom into project image'}
                        role="button"
                        tabIndex={0}
                        onKeyDown={event => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                handleZoomToggle();
                            }
                        }}
                    >
                        <img 
                            src={imgUrl} 
                            alt={title} 
                            className="modal-zoom-image"
                            draggable={false}
                            style={{
                                transform: isImageZoomed
                                    ? `translate(${zoomOffset.x}px, ${zoomOffset.y}px) scale(1.8)`
                                    : 'translate(0px, 0px) scale(1)',
                                transition: isDragging ? 'none' : undefined
                            }}
                        />
                    </div>
                    <p className="zoom-hint">
                        {isImageZoomed ? 'Drag to explore • Click or tap to exit zoom' : 'Click or tap to zoom in'}
                    </p>
                </Modal.Body>
            </Modal>
        </Modal>
    );
}

export default ProjectModal;
